import { messagingApi } from '@/api/MessagingApi'
import type { ChatMessage } from '@/api/types/MessagingApi'
import { isApiRequestError } from '@/errors/ApiRequestError'
import { useQuery } from '@tanstack/react-query'

export const CHAT_MESSAGES_QUERY_KEY = ['chat-messages']

/** Polling cadence: the open thread asks often, the home tile rarely; TanStack takes the shortest. */
export const CHAT_POLL_OPEN_MS = 10_000
export const CHAT_POLL_IDLE_MS = 60_000

const byCreatedAt = (a: ChatMessage, b: ChatMessage) =>
	a.createdAt.localeCompare(b.createdAt)

/**
 * The patient's support thread. Polled while any screen shows it; nothing is pushed to
 * patients yet. A 404 means the endpoint is not deployed, which callers treat as "no chat".
 */
const useChatMessages = (pollMs = CHAT_POLL_IDLE_MS) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: CHAT_MESSAGES_QUERY_KEY,
		queryFn: () => messagingApi.listChatMessages(),
		// Unread is counted here, not taken from the server, so it clears the moment the thread is
		// open and marks the replies seen, without waiting on a poll.
		select: (response) => {
			const messages = [...(response.messages ?? [])].sort(byCreatedAt)
			return {
				messages,
				unreadCount: messages.filter(
					(m) => m.direction === 'OUTGOING' && m.consumedAt === null
				).length,
			}
		},
		refetchInterval: pollMs,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
		retry: (failureCount, err) =>
			!(isApiRequestError(err) && err.status === 404) && failureCount < 2,
	})

	const isUnavailable = isError && isApiRequestError(error) && error.status === 404

	return {
		messages: data?.messages ?? [],
		unreadCount: data?.unreadCount ?? 0,
		isLoading,
		isError,
		isUnavailable,
		refetch,
	}
}

export { useChatMessages }
